import * as React from 'react'
import { ReactElement, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { FixedPriceMutationDto, FixedPriceOverviewDto, FixedPriceTypeVariant } from '../../../config/registrationConfig'
import ModifyRegistrationRow, {
    ModifyRegistrationColumnProps,
    ModifyRegistrationColumns,
    ModifyRegistrationRowProps,
    useRegistrationRowStyles,
} from '../Registration/ModifyRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { FixedPriceListProps, getEffectiveFixedPriceRate } from './FixedPriceList'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'
import useVatTypes from '../../../hooks/useVatTypes'
import FormikField from '../../Formik/FormikField'
import MoneyInput, { MoneyInputProps } from '../../Input/MoneyInput'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import { PricePrecedence } from '../../../config/finance/financeConfig'
import useCurrency from '../../../hooks/useCurrency'
import { usePrevious } from '@fluentui/react-hooks'
import useFixedPriceType from '../../../hooks/useFixedPriceType'
import FormikRichTextField from '../../Formik/FormikRichTextField'
import FormikInputField from '../../Formik/FormikInputField'
import FixedPriceTypePicker, { FixedPriceTypePickerProps } from '../../TagPicker/FixedPriceTypePicker'
import { useTranslation } from 'react-i18next'
import { InfoLabel, LabelProps } from '@fluentui/react-components'

type Props = ModifyRegistrationRowProps<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps>
type ColumnProps = ModifyRegistrationColumnProps<FixedPriceListProps>

function Columns(props: ColumnProps): ReactElement {
    const registrationStyles = useRegistrationRowStyles()
    const { t: translate } = useTranslation()

    const { values, setFieldValue, setValues } = useFormikContext<FixedPriceMutationDto>()
    const prevValues = usePrevious(values)

    const { fixedPriceTypes, getFixedPriceType } = useFixedPriceTypes()
    const { vatTypes, getVatType } = useVatTypes()

    const { currency } = useCurrency(values.currency)
    const [fixedPriceType] = useFixedPriceType(values.type)

    useEffect(() => {
        const newFixedPriceType = getFixedPriceType(values.type)
        const newVatType = getVatType(values.vatType)

        if ((!newFixedPriceType || !newFixedPriceType?.includingVat) && values.rate && values.rate !== prevValues?.rate) {
            setFieldValue(
                'rateTotal',
                getEffectiveFixedPriceRate(values.rate, values.rateTotal, newFixedPriceType, newVatType, PricePrecedence.Total),
                false,
            )
        }
    }, [values.rate])

    useEffect(() => {
        const newFixedPriceType = getFixedPriceType(values.type)
        const newVatType = getVatType(values.vatType)

        if (newFixedPriceType?.includingVat && values.rateTotal && values.rateTotal !== prevValues?.rateTotal) {
            setFieldValue(
                'rate',
                getEffectiveFixedPriceRate(values.rate, values.rateTotal, newFixedPriceType, newVatType, PricePrecedence.Subtotal),
                false,
            )
        }
    }, [values.rateTotal])

    const useRateFields = useMemo<boolean>(() => !fixedPriceType || ![FixedPriceTypeVariant.Mock].includes(fixedPriceType?.variant), [fixedPriceType])

    switch (props.column.key) {
        case 'type':
            return (
                fixedPriceTypes.length > 0 && (
                    <FormikComboboxField<FixedPriceMutationDto, FixedPriceTypePickerProps>
                        fieldName={'type'}
                        component={FixedPriceTypePicker}
                        size={'small'}
                        componentProps={{
                            singleSelect: true,
                            required: true,
                            selectedOptions: [values.type],
                            currency: values.currency,
                            date: values.date,
                            onOptionsSelect: (_, selectedOptions, selectedEntities) => {
                                const newFixedPriceType = selectedEntities?.[0]

                                setValues(
                                    oldValues => ({
                                        ...oldValues,
                                        type: selectedOptions?.[0],
                                        vatType: newFixedPriceType?.vatType || vatTypes[0].id,
                                        name: newFixedPriceType?.name,
                                        description: newFixedPriceType?.description,
                                        longDescription: newFixedPriceType?.longDescription,
                                        rate: (newFixedPriceType?.includingVat ? undefined : newFixedPriceType?.rate) ?? '0.00',
                                        rateTotal: newFixedPriceType?.includingVat ? newFixedPriceType?.rate : undefined,
                                    }),
                                    false,
                                )
                            },
                            styles: {
                                control: registrationStyles.type,
                            },
                        }}
                    />
                )
            )
        case 'description':
            return (
                <div>
                    <FormikInputField<FixedPriceMutationDto>
                        className={registrationStyles.descriptionWithInfo}
                        renderLabel={{
                            children: (_: unknown, slotProps: LabelProps) => (
                                <InfoLabel {...slotProps} info={translate('fixedprice.edit.name.info')} />
                            ),
                        }}
                        fieldName={'name'}
                        componentProps={{
                            size: 'small',
                            className: registrationStyles.description,
                        }}
                    />
                    <FormikInputField<FixedPriceMutationDto>
                        className={registrationStyles.descriptionWithInfo}
                        renderLabel={{
                            children: (_: unknown, slotProps: LabelProps) => (
                                <InfoLabel {...slotProps} info={translate('registration.edit.description.info')} />
                            ),
                        }}
                        fieldName={'description'}
                        componentProps={{
                            size: 'small',
                            className: registrationStyles.description,
                        }}
                    />
                    <FormikRichTextField<FixedPriceMutationDto>
                        className={registrationStyles.descriptionWithInfo}
                        renderLabel={{
                            children: (_: unknown, slotProps: LabelProps) => (
                                <InfoLabel {...slotProps} info={translate('fixedprice.edit.longdescription.info')} />
                            ),
                        }}
                        fieldName={'longDescription'}
                    />
                </div>
            )
        case 'rate':
            return (
                useRateFields && (
                    <>
                        {fixedPriceType?.includingVat ? (
                            <MoneyText currency={currency} amount={values.rate} align="right" />
                        ) : (
                            <FormikField<MoneyInputProps, FixedPriceMutationDto>
                                fieldName={'rate'}
                                component={MoneyInput}
                                componentProps={{
                                    size: 'small',
                                    currency: values.currency,
                                    allowNegative: true,
                                    onCommit: (_, value: string) => setFieldValue('rate', value),
                                    className: registrationStyles.rate,
                                    input: { className: registrationStyles.rateInput },
                                }}
                            />
                        )}
                    </>
                )
            )
        case 'vat':
            return (
                useRateFields && (
                    <FormikComboboxField<FixedPriceMutationDto, VatTypeComboboxProps>
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        componentProps={{
                            size: 'small',
                            selectedOptions: [values.vatType],
                            onOptionSelect: (_, data) => setFieldValue('vatType', data.selectedOptions?.[0]),
                            className: registrationStyles.vat,
                            input: { className: registrationStyles.vatInput },
                        }}
                    />
                )
            )
        case 'total':
            return (
                useRateFields && (
                    <>
                        {fixedPriceType?.includingVat ? (
                            <FormikField<MoneyInputProps, FixedPriceMutationDto>
                                fieldName={'rateTotal'}
                                component={MoneyInput}
                                componentProps={{
                                    size: 'small',
                                    currency: values.currency,
                                    allowNegative: true,
                                    onCommit: (_, value: string) => setFieldValue('rateTotal', value),
                                    className: registrationStyles.rate,
                                    input: { className: registrationStyles.rateInput },
                                }}
                            />
                        ) : (
                            <MoneyText currency={currency} amount={values.rateTotal} align="right" />
                        )}
                    </>
                )
            )
    }
    return <ModifyRegistrationColumns<FixedPriceMutationDto, FixedPriceListProps> {...props} />
}

export default function ModifyFixedPriceRow(props: Props): ReactElement {
    return <ModifyRegistrationRow<FixedPriceOverviewDto, FixedPriceMutationDto, FixedPriceListProps> {...props} columns={Columns} />
}
